import { useMemo, useState } from "react";
import { categories, feedItems } from "@/mocks/gossipData";

function formatHeat(n: number) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + " 千万";
  return (n / 10000).toFixed(0) + " 万";
}

const tagStyle: Record<string, string> = {
  爆: "bg-primary-500 text-background-50",
  热: "bg-accent-500 text-accent-950",
  新: "bg-secondary-500 text-background-50",
};

export default function Feed() {
  const [active, setActive] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [empty, setEmpty] = useState(false);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const res = feedItems.filter((it) => {
      const matchCat = active === "all" || it.cat === active;
      const matchKw = !kw || it.title.toLowerCase().includes(kw) || it.excerpt.toLowerCase().includes(kw);
      return matchCat && matchKw;
    });
    return res;
  }, [active, keyword]);

  return (
    <section id="feed" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary-600">Feed Board</p>
            <h2 className="font-heading mt-2 text-2xl font-black text-foreground-950 md:text-4xl">瓜料速览</h2>
            <p className="mt-3 max-w-xl text-sm text-foreground-600">
              按栏目速读近期动态，支持搜索关键词与切换分类，快速定位你关心的瓜。
            </p>
          </div>

          <label className="flex h-11 w-full max-w-sm items-center gap-2 rounded-md bg-background-50 px-3 ring-1 ring-background-300 focus-within:ring-2 focus-within:ring-primary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索瓜料 / 关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button onClick={() => setKeyword("")} aria-label="清空搜索">
                <i className="ri-close-circle-fill text-foreground-300 hover:text-primary-500"></i>
              </button>
            )}
          </label>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2">
          {categories.map((c) => {
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                onClick={() => {
                  setActive(c.key);
                  setEmpty(false);
                }}
                className={
                  "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
                  (isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:bg-primary-100 hover:text-primary-700")
                }
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl bg-background-50 py-16 text-center ring-1 ring-background-200">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-base font-medium text-foreground-700">暂时没有找到相关瓜料</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词，或切换到其它栏目再试试</p>
            <button
              onClick={() => {
                setKeyword("");
                setActive("all");
                setEmpty(true);
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              查看全部
            </button>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-xl bg-background-50 ring-1 ring-background-200 transition-transform hover:-translate-y-1"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span
                    className={
                      "absolute right-2 top-2 inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-xs font-bold " +
                      (tagStyle[item.tag] || "bg-background-50 text-foreground-700")
                    }
                  >
                    {item.tag}
                  </span>
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-foreground-950/55 px-2 py-0.5 text-[11px] text-background-50 backdrop-blur">
                    <i className="ri-fire-line"></i>
                    {formatHeat(item.heat)}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-[15px] font-bold leading-snug text-foreground-900 group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.excerpt}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                    <span>{item.date}</span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                      查看详情
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}